import { EN_TETE } from './actions.js';
import type { CSSProperties, FormEvent, ReactNode } from 'react';
import { PHASES } from '../../../../../packages/contracts/src/entities/index.js';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import type { EtatEditeur } from './editeur.js';
import { LIBELLE_ETAT, resumeParcours } from './parcours.js';
import type { Etape } from './types.js';

export interface EditeurEtapesProps {
  etapes: readonly Etape[];
  etat: EtatEditeur;
  onMonter: (id: string) => void;
  onDescendre: (id: string) => void;
  onDemanderSaut: (id: string) => void;
  onMotif: (valeur: string) => void;
  onConfirmerSaut: () => void;
  onAnnulerSaut: () => void;
  onAjouter: (phase: string, libelle: string, apresEtapeId: string | null) => void;
  onFermer: () => void;
}

const panneau: CSSProperties = {
  background: COULEURS.blanc,
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 12,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};
const champ: CSSProperties = {
  font: 'inherit',
  minHeight: CIBLE_MIN,
  padding: '0 12px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
};

interface ChampsAjout {
  elements: { phase: { value: string }; libelle: { value: string }; apres: { value: string } };
}

/** Éditeur d'étapes du dossier : réordonner, sauter avec motif, ajouter (tutelle, prescription, sommation…). */
export function EditeurEtapes(props: EditeurEtapesProps): ReactNode {
  const { etapes, etat } = props;
  if (!etat.ouvert) return null;
  const parcours = resumeParcours(etapes);
  const soumettre = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { phase, libelle, apres } = (e.currentTarget as unknown as ChampsAjout).elements;
    const texte = libelle.value.trim();
    if (texte === '') return;
    props.onAjouter(phase.value, texte, apres.value === '' ? null : apres.value);
  };
  return (
    <section aria-label="Éditeur d’étapes" style={panneau}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Modifier les étapes</h2>
        <button type="button" aria-label="Fermer l’éditeur d’étapes" style={styleBouton} onClick={props.onFermer}>
          Fermer
        </button>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {parcours.map((p) => (
          <li
            key={p.id}
            style={{
              background: COULEURS.surface,
              borderRadius: 8,
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
              <span>
                <b>{p.code}</b> {p.libelle} <span style={{ color: COULEURS.ardoise }}>({LIBELLE_ETAT[p.etat]})</span>
              </span>
              <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  aria-label={`Monter l’étape ${p.libelle}`}
                  disabled={etat.occupe}
                  style={styleBouton}
                  onClick={() => props.onMonter(p.id)}
                >
                  Monter
                </button>
                <button
                  type="button"
                  aria-label={`Descendre l’étape ${p.libelle}`}
                  disabled={etat.occupe}
                  style={styleBouton}
                  onClick={() => props.onDescendre(p.id)}
                >
                  Descendre
                </button>
                {p.etat !== 'saute' && p.etat !== 'fait' ? (
                  <button
                    type="button"
                    aria-label={`Sauter l’étape ${p.libelle}`}
                    disabled={etat.occupe}
                    style={styleBouton}
                    onClick={() => props.onDemanderSaut(p.id)}
                  >
                    Sauter
                  </button>
                ) : null}
              </span>
            </div>
            {p.motifSaut ? <span style={{ fontSize: 13, color: COULEURS.ardoise }}>{p.motifSaut}</span> : null}
            {etat.sautId === p.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  type="text"
                  aria-label="Motif du saut"
                  value={etat.motif}
                  style={champ}
                  onChange={(e) => props.onMotif(e.target.value)}
                />
                {etat.erreur ? (
                  <p role="alert" style={{ margin: 0, color: COULEURS.rouge, fontSize: 14 }}>
                    {etat.erreur}
                  </p>
                ) : null}
                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    aria-label="Confirmer le saut"
                    disabled={etat.occupe}
                    style={styleBoutonPrincipal}
                    onClick={props.onConfirmerSaut}
                  >
                    Confirmer le saut
                  </button>
                  <button type="button" aria-label="Annuler le saut" style={styleBouton} onClick={props.onAnnulerSaut}>
                    Annuler
                  </button>
                </span>
              </div>
            ) : null}
          </li>
        ))}
      </ol>
      <form onSubmit={soumettre} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <select name="phase" aria-label="Phase de la nouvelle étape" style={champ} defaultValue="P3">
          {PHASES.map((ph) => (
            <option key={ph.code} value={ph.code}>
              {ph.code} · {ph.libelle}
            </option>
          ))}
        </select>
        <input name="libelle" type="text" aria-label="Libellé de la nouvelle étape" style={{ ...champ, flex: '1 1 220px' }} />
        <select name="apres" aria-label="Placer après" style={champ} defaultValue="">
          <option value="">À la fin du parcours</option>
          <option value={EN_TETE}>En tête du parcours</option>
          {parcours.map((p) => (
            <option key={p.id} value={p.id}>
              Après {p.code} · {p.libelle}
            </option>
          ))}
        </select>
        <button type="submit" disabled={etat.occupe} style={styleBoutonPrincipal}>
          Ajouter l’étape
        </button>
      </form>
      {etat.erreur && etat.sautId === null ? (
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge, fontSize: 14 }}>
          {etat.erreur}
        </p>
      ) : null}
    </section>
  );
}
